import { useState } from "react";
import { T } from "../utils/theme";
import { api, ENDPOINTS, ACCESS_REASONS, formatDate } from "../utils/api";
import { useFetch, useToast, useModal } from "../hooks";
import {
  Card, Spinner, EmptyState, SectionHeader, Modal, Field, LoadingBtn, AlertBox, ToastStack,
} from "../components/UI";
import { Key, Lock, Unlock, Shield, AlertTriangle, CheckCircle, XCircle, Eye, EyeOff, RefreshCw, Clock } from "lucide-react";

const KeyStatusCard = ({ token }) => {
  const { data: status, loading, refetch } = useFetch(ENDPOINTS.encKeyStatus, token);
  const [password, setPassword] = useState("");
  const [privateKey, setPrivateKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [decrypting, setDecrypting] = useState(false);
  const { toasts, show, remove } = useToast();

 const generateKeys = async () => {
  if (!password) return;

  setGenerating(true);

  try {
    const res = await api(
      ENDPOINTS.encGenerate,
      {
        method: "POST",
        body: JSON.stringify({ password }),
      },
      token
    );

    console.log("Réponse API :", res); // 👈 voir dans console

    show(res.message || "🔑 Clés RSA générées avec succès !");
    
    setPassword("");
    refetch();

  } catch (e) {
    console.error(e);
    show(e.message, "error");
  } finally {
    setGenerating(false);
  }
};

 
  const decryptPrivate = async () => {
    if (!password) return;
    setDecrypting(true);
    try {
      const d = await api(ENDPOINTS.encDecryptPriv, { method: "POST", body: JSON.stringify({ password }) }, token);
      setPrivateKey(d.data?.privateKey || "Clé déchiffrée avec succès");
      show("Clé privée déchiffrée !");
      setPassword("");
    } catch (e) { show(e.message, "error"); } finally { setDecrypting(false); }
  };

  if (loading) return <Spinner />;
  const hasKeys = status?.hasKeys || status?.hasActiveKeys;

  return (
    <>
      <div className="grid-2" style={{ marginBottom: 20 }}>
        {/* Key Status */}
        <Card style={{ padding: 22 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: `${T.blue}18`, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Key size={17} color={T.blue} />
            </div>
            <div>
              <div style={{ fontFamily: T.display, fontWeight: 700, fontSize: 14 }}>Mes Clés RSA</div>
              <div style={{ fontSize: 11, color: T.muted }}>Chiffrement de bout en bout</div>
            </div>
          </div>

          <div style={{
            display: "flex", alignItems: "center", gap: 8, padding: "10px 14px",
            background: hasKeys ? `${T.green}0f` : `${T.red}0f`,
            border: `1px solid ${hasKeys ? T.green : T.red}30`,
            borderRadius: 9, marginBottom: 18,
          }}>
            {hasKeys ? <CheckCircle size={15} color={T.green} /> : <AlertTriangle size={15} color={T.red} />}
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: hasKeys ? T.green : T.red }}>
                {hasKeys ? "Clés actives" : "Aucune clé générée"}
              </div>
              {status?.keyVersion && <div style={{ fontSize: 10, color: T.muted }}>Version {status.keyVersion}</div>}
            </div>
          </div>

          {status?.publicKey && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 10, color: T.muted, fontWeight: 700, marginBottom: 6 }}>CLÉ PUBLIQUE</div>
              <div className="code-block">{status.publicKey}</div>
            </div>
          )}

          <Field label={hasKeys ? "Mot de passe pour déchiffrer" : "Mot de passe de chiffrement"}>
            <input
              type="password"
              placeholder="Mot de passe fort..."
              value={password}
              onChange={e => setPassword(e.target.value)}
            />
          </Field>

          {!hasKeys ? (
            <LoadingBtn
              loading={generating}
              className="btn btn-primary"
              style={{ width: "100%", justifyContent: "center" }}
              onClick={generateKeys}
              disabled={!password}
            >
              <Key size={14} /> Générer les clés RSA
            </LoadingBtn>
          ) : (
            <button
              className="btn btn-ghost"
              style={{ width: "100%", justifyContent: "center" }}
              onClick={decryptPrivate}
              disabled={decrypting || !password}
            >
              {decrypting ? <RefreshCw size={13} className="spin" /> : <Unlock size={13} />}
              Déchiffrer la clé privée
            </button>
          )}
        </Card>

        {/* Private Key Display */}
        <Card style={{ padding: 22 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: `${T.yellow}18`, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Lock size={17} color={T.yellow} />
            </div>
            <div>
              <div style={{ fontFamily: T.display, fontWeight: 700, fontSize: 14 }}>Clé Privée</div>
              <div style={{ fontSize: 11, color: T.muted }}>Sensible — Ne jamais partager</div>
            </div>
            {privateKey && (
              <button className="btn btn-ghost btn-sm btn-icon" style={{ marginLeft: "auto" }} onClick={() => setShowKey(!showKey)}>
                {showKey ? <EyeOff size={13} /> : <Eye size={13} />}
              </button>
            )}
          </div>

          {privateKey ? (
            <>
              <AlertBox type="warning">
                Votre clé privée est déchiffrée et affichée. Ne la transmettez jamais à quiconque.
              </AlertBox>
              <div style={{ marginTop: 14 }}>
                <div className="code-block" style={{ filter: showKey ? "none" : "blur(5px)", transition: "filter .3s", userSelect: showKey ? "text" : "none", color: T.yellow }}>
                  {privateKey}
                </div>
              </div>
            </>
          ) : (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "32px 0", color: T.muted, textAlign: "center" }}>
              <Lock size={32} style={{ opacity: .2, marginBottom: 12 }} />
              <div style={{ fontSize: 13 }}>Clé privée verrouillée</div>
              <div style={{ fontSize: 11, marginTop: 4 }}>Déchiffrez avec votre mot de passe</div>
            </div>
          )}
        </Card>
      </div>
      <ToastStack toasts={toasts} remove={remove} />
    </>
  );
};

const AccessRequestModal = ({ open, onClose, onSubmit, loading }) => {
  const [form, setForm] = useState({ reportId: "", reason: "litige", justification: "" });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const handleSubmit = (e) => { e.preventDefault(); onSubmit(form); };

  return (
    <Modal open={open} onClose={onClose} title="Demande d'Accès d'Urgence" width={500}>
      <AlertBox type="warning" style={{ marginBottom: 18 }}>
        Cette demande nécessite l'approbation d'un second administrateur (contrôle dual). Une piste d'audit sera créée.
      </AlertBox>
      <form onSubmit={handleSubmit}>
        <Field label="ID du rapport">
          <input placeholder="UUID du rapport..." value={form.reportId} onChange={e => set("reportId", e.target.value)} required />
        </Field>
        <Field label="Raison">
          <select value={form.reason} onChange={e => set("reason", e.target.value)}>
            {ACCESS_REASONS.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
          </select>
        </Field>
        <Field label="Justification détaillée">
          <textarea
            placeholder="Décrivez précisément la raison de l'accès d'urgence..."
            value={form.justification}
            onChange={e => set("justification", e.target.value)}
            rows={4}
            style={{ resize: "vertical" }}
            required
          />
        </Field>
        <div style={{ display: "flex", gap: 10 }}>
          <button type="button" className="btn btn-ghost" style={{ flex: 1, justifyContent: "center" }} onClick={onClose}>Annuler</button>
          <LoadingBtn loading={loading} className="btn btn-warning" style={{ flex: 1, justifyContent: "center" }}>
            <Shield size={13} /> Soumettre la demande
          </LoadingBtn>
        </div>
      </form>
    </Modal>
  );
};

const PendingRequestCard = ({ req, onApprove, onRefuse, loading }) => (
  <Card style={{ padding: 18, marginBottom: 10 }}>
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14 }}>
      <div style={{ flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <AlertTriangle size={15} color={T.yellow} />
          <span style={{ fontFamily: T.display, fontWeight: 700, fontSize: 13 }}>Demande d'accès d'urgence</span>
          <span className="badge" style={{ color: T.yellow, background: `${T.yellow}12` }}>
            <Clock size={9} /> En attente
          </span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
          {[
            { l: "Rapport", v: req.reportId?.slice(0,16) + "..." },
            { l: "Raison",  v: req.reason },
            { l: "Demandé par", v: req.requesterId?.slice(0,12) },
            { l: "Date",    v: formatDate(req.createdAt) },
          ].map((f, i) => (
            <div key={i}>
              <span style={{ fontSize: 10, color: T.muted, fontWeight: 700, marginRight: 6 }}>{f.l}:</span>
              <span style={{ fontSize: 12, color: T.text }}>{f.v}</span>
            </div>
          ))}
        </div>
        {req.justification && (
          <div style={{ fontSize: 12, color: T.textSub, background: T.bg, borderRadius: 8, padding: "8px 12px", fontStyle: "italic" }}>
            "{req.justification}"
          </div>
        )}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, flexShrink: 0 }}>
        <button className="btn btn-success btn-sm" onClick={() => onApprove(req.id)} disabled={loading}>
          <CheckCircle size={12} /> Approuver
        </button>
        <button className="btn btn-danger btn-sm" onClick={() => onRefuse(req.id)} disabled={loading}>
          <XCircle size={12} /> Refuser
        </button>
      </div>
    </div>
  </Card>
);

const EncryptionPage = ({ token, user }) => {
  const { data: pendingReqs, loading: pendingLoading, refetch: refetchPending } = useFetch(ENDPOINTS.encPending, token);
  const { toasts, show, remove } = useToast();
  const requestModal = useModal();
  const [actionLoading, setActionLoading] = useState(false);

  const respondToRequest = async (requestId, approve) => {
    setActionLoading(true);
    try {
      await api(ENDPOINTS.encRespond(requestId), {
        method: "PUT",
        body: JSON.stringify({ approve, comment: approve ? "Approuvé par l'administrateur" : "Refusé" }),
      }, token);
      show(approve ? "✅ Accès approuvé !" : "❌ Accès refusé !");
      refetchPending();
    } catch (e) { show(e.message, "error"); } finally { setActionLoading(false); }
  };

  const submitAccessRequest = async (form) => {
    setActionLoading(true);
    try {
      await api(ENDPOINTS.encRequestAccess, { method: "POST", body: JSON.stringify(form) }, token);
      show("Demande d'accès soumise — en attente d'approbation !");
      requestModal.close();
      refetchPending();
    } catch (e) { show(e.message, "error"); } finally { setActionLoading(false); }
  };

  // Normalise la réponse API en tableau quoi qu'il arrive
  const pendingList = Array.isArray(pendingReqs)
    ? pendingReqs
    : Array.isArray(pendingReqs?.data)
      ? pendingReqs.data
      : [];

  const pendingCount = pendingList.length;

  return (
    <div className="fade-up" style={{ padding: "24px 28px", overflowY: "auto", flex: 1 }}>
      <SectionHeader
        title="Chiffrement E2E & Sécurité"
        sub="Gestion des clés RSA et accès administrateur d'urgence"
        action={
          <button className="btn btn-warning" onClick={requestModal.openWith}>
            <Shield size={14} /> Demander un accès d'urgence
          </button>
        }
      />

      {/* Info banner */}
      <AlertBox type="info" style={{ marginBottom: 22 }}>
        Le système E2E utilise RSA-2048. Les rapports chiffrés ne peuvent être lus qu'avec la clé privée correspondante.
        Le contrôle dual garantit qu'aucun administrateur seul ne peut accéder aux données sensibles.
      </AlertBox>

      {/* Key management */}
      <KeyStatusCard token={token} />

      {/* Dual Control Pending */}
      <div style={{ marginBottom: 22 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <div>
            <div style={{ fontFamily: T.display, fontWeight: 700, fontSize: 16, color: T.text }}>
              Demandes d'Accès en Attente
              {pendingCount > 0 && (
                <span style={{
                  marginLeft: 10, background: T.red, color: "#fff",
                  borderRadius: 6, padding: "2px 8px", fontSize: 11,
                }}>
                  {pendingCount}
                </span>
              )}
            </div>
            <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>Contrôle dual — approuvez ou refusez les demandes des autres admins</div>
          </div>
        </div>

        {pendingLoading ? <Spinner /> : pendingCount === 0 ? (
          <Card style={{ padding: 0 }}>
            <EmptyState icon={Lock} title="Aucune demande en attente" sub="Toutes les demandes d'accès ont été traitées" />
          </Card>
        ) : (
          pendingList.map((req, i) => (
            <PendingRequestCard
              key={req.id || i}
              req={req}
              onApprove={(id) => respondToRequest(id, true)}
              onRefuse={(id) => respondToRequest(id, false)}
              loading={actionLoading}
            />
          ))
        )}
      </div>

      {/* SuperAdmin only: Audit info */}
      {user?.role === "superadmin" && (
        <Card style={{ padding: 20 }}>
          <div style={{ fontFamily: T.display, fontWeight: 700, fontSize: 15, marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
            <Shield size={16} color={T.purple} /> Audit & Historique
          </div>
          <AlertBox type="info">
            En tant que Super Admin, vous pouvez consulter l'historique complet des accès admin via le endpoint{" "}
            <code style={{ color: T.cyan }}>/encryption/admin/access-history/:reportId</code>.
            Utilisez l'ID du rapport concerné.
          </AlertBox>
        </Card>
      )}

      <AccessRequestModal
        open={requestModal.open}
        onClose={requestModal.close}
        onSubmit={submitAccessRequest}
        loading={actionLoading}
      />

      <ToastStack toasts={toasts} remove={remove} />
    </div>
  );
};

export default EncryptionPage;
